/**
 * Announcements: a message the Arcanum Customer Success team posts to every
 * person on a workspace, from the Customer Success portal.
 *
 * A person sees each announcement once, as a pop-up, the next time they open
 * Numa before its "show until" date. After that it is only in the
 * Announcements history on the Notifications page. When several are waiting,
 * the pop-up shows the newest one and says how many earlier ones there are;
 * closing it counts them all as seen.
 *
 * REVOKED. The portal can revoke one: the row stays (so the portal still
 * lists it, with who revoked it and when) but carries `revoked_at`, and
 * nobody on the workspace sees it again, as a pop-up or in the history. The
 * portal can restore it. A revoked row is cleaned up by the same TTL as any
 * other.
 *
 * STORAGE. The notifications table, in two partitions of their own, the same
 * way the preferences rows live there (`preferences.ts`):
 *
 *   user_id: 'ANNOUNCEMENT'              notification_id: <announcement id>
 *     one row per announcement, written by the portal into the workspace's
 *     account (it assumes ArcanumAIAccess). The portal is the only writer.
 *
 *   user_id: 'ANNOUNCEMENT_SEEN#<sub>'   notification_id: <announcement id>
 *     one row per person per announcement they have seen, written by
 *     notifications-api. It is the record that stops the pop-up coming back
 *     on another browser or on the phone.
 *
 * Neither row carries `created_at` or `schedule_id`, so neither ever appears
 * in a person's bell list, their unread count or the schedule index. Both
 * carry the table's TTL (`expires_at`, epoch SECONDS), set to the show-until
 * date plus HISTORY_DAYS, so the history keeps an announcement for 90 days
 * after it stops popping up and then the table deletes it.
 *
 * PRODUCT TIPS. A callout the frontend shows beside a control, once per person
 * (the NEW note on the chat's model picker). Its text lives in the frontend;
 * PRODUCT_TIPS below only names which tips exist, so a "seen" call cannot
 * write a row for anything else. Closing one writes a seen row in the same
 * ANNOUNCEMENT_SEEN#<sub> partition, keyed TIP#<tip>, so it stays closed on
 * every browser and on the phone. That row has no TTL: a closed tip stays
 * closed for good. The announcements GET returns the caller's closed tips.
 *
 * No dependencies: notifications-api, the frontend and the portal all import
 * this file.
 */

export const ANNOUNCEMENT_PARTITION = 'ANNOUNCEMENT';
export const ANNOUNCEMENT_SEEN_PARTITION_PREFIX = 'ANNOUNCEMENT_SEEN#';

export const announcementSeenPartition = (sub: string): string => `${ANNOUNCEMENT_SEEN_PARTITION_PREFIX}${sub}`;

/** Every product tip the frontend may show. Add one here before it ships. */
export const PRODUCT_TIPS = ['model-switching'] as const;
export type ProductTip = (typeof PRODUCT_TIPS)[number];

const PRODUCT_TIP_PREFIX = 'TIP#';

/** The id a closed tip's seen row carries. */
export const productTipSeenId = (tip: ProductTip): string => `${PRODUCT_TIP_PREFIX}${tip}`;

/** The tip a seen id names, or null when it names no tip on the list. */
export const productTipFromSeenId = (id: string): ProductTip | null => {
  if (!id.startsWith(PRODUCT_TIP_PREFIX)) return null;
  const tip = id.slice(PRODUCT_TIP_PREFIX.length);
  return (PRODUCT_TIPS as readonly string[]).includes(tip) ? (tip as ProductTip) : null;
};

export const ANNOUNCEMENT_AUDIENCES = ['everyone', 'admins'] as const;
export type AnnouncementAudience = (typeof ANNOUNCEMENT_AUDIENCES)[number];

export const ANNOUNCEMENT_LIMITS = {
  title: 120,
  body: 4000,
  linkLabel: 40,
  linkUrl: 2000,
  /** The pop-up runs for at most this long after it is posted. */
  maxShowDays: 90,
  /** What the portal offers when nobody changes it. */
  defaultShowDays: 14,
  /** How long the history keeps an announcement after it stops popping up. */
  historyDays: 90,
  /** How many ids one "seen" call may mark. */
  maxSeenIds: 25,
} as const;

const DAY_MS = 24 * 60 * 60 * 1000;

/** The row as stored. Times are epoch milliseconds except `expires_at`. */
export interface AnnouncementRow {
  user_id: typeof ANNOUNCEMENT_PARTITION;
  notification_id: string;
  title: string;
  /** Markdown. */
  body: string;
  link_url?: string;
  link_label?: string;
  audience: AnnouncementAudience;
  published_at: number;
  show_until: number;
  updated_at?: number;
  /** Portal only: who posted it. Never sent to the workspace's people. */
  posted_by?: string;
  /** Portal only: what it was sent to, e.g. "Deployment group: Pilot". */
  sent_to?: string;
  /** How many people have seen it on this workspace. */
  seen_count?: number;
  /** Set while it is revoked: hidden from everybody on the workspace. */
  revoked_at?: number;
  /** Portal only: who revoked it. */
  revoked_by?: string;
  /** TTL, epoch seconds. */
  expires_at: number;
}

/** What a person on the workspace is sent. */
export interface Announcement {
  id: string;
  title: string;
  body: string;
  linkUrl?: string;
  linkLabel?: string;
  audience: AnnouncementAudience;
  publishedAt: number;
  showUntil: number;
  updatedAt?: number;
  seen: boolean;
}

/** What the portal submits when it posts or edits one. */
export interface AnnouncementDraft {
  title: string;
  body: string;
  linkUrl?: string;
  linkLabel?: string;
  audience: AnnouncementAudience;
  showUntil: number;
}

/**
 * Where the pop-up's button may go: a full https:// address, or a page inside
 * the workspace itself ("/files"), which every workspace has at the same
 * path. Nothing else: a link in a pop-up every person sees must not run a
 * script or leave https.
 */
export const isSafeLinkUrl = (raw: string): boolean => {
  if (isWorkspacePath(raw)) return true;
  try {
    const url = new URL(raw);
    return url.protocol === 'https:' && Boolean(url.hostname);
  } catch {
    return false;
  }
};

/** "/files", "/support?tab=release-notes": a page in this workspace. Never
 *  "//host", which a browser reads as another site. */
export const isWorkspacePath = (raw: string): boolean =>
  /^\/(?![/\\])/.test(raw) && !/[\s\\]/.test(raw) && raw.length <= ANNOUNCEMENT_LIMITS.linkUrl;

/** Every problem with a draft, in the words the portal shows. Empty = valid. */
export const validateAnnouncementDraft = (draft: AnnouncementDraft, now: number): string[] => {
  const errors: string[] = [];
  const title = draft.title?.trim() ?? '';
  const body = draft.body?.trim() ?? '';
  const linkUrl = draft.linkUrl?.trim() ?? '';
  const linkLabel = draft.linkLabel?.trim() ?? '';

  if (!title) errors.push('Enter a title.');
  if (title.length > ANNOUNCEMENT_LIMITS.title)
    errors.push(`Keep the title to ${ANNOUNCEMENT_LIMITS.title} characters or fewer.`);
  if (!body) errors.push('Enter a message.');
  if (body.length > ANNOUNCEMENT_LIMITS.body)
    errors.push(`Keep the message to ${ANNOUNCEMENT_LIMITS.body} characters or fewer.`);
  if (!ANNOUNCEMENT_AUDIENCES.includes(draft.audience)) errors.push('Choose who sees it.');

  if (linkUrl || linkLabel) {
    if (!linkUrl) errors.push('Enter the link address, or clear the button text.');
    else if (linkUrl.length > ANNOUNCEMENT_LIMITS.linkUrl || !isSafeLinkUrl(linkUrl))
      errors.push('The link must be a full https:// address, or a Numa page such as /files.');
    if (!linkLabel) errors.push('Enter the button text, or clear the link address.');
    else if (linkLabel.length > ANNOUNCEMENT_LIMITS.linkLabel)
      errors.push(`Keep the button text to ${ANNOUNCEMENT_LIMITS.linkLabel} characters or fewer.`);
  }

  if (!Number.isFinite(draft.showUntil) || draft.showUntil <= now) {
    errors.push('Choose a "show until" date in the future.');
  } else if (draft.showUntil > now + ANNOUNCEMENT_LIMITS.maxShowDays * DAY_MS) {
    errors.push(`Choose a "show until" date no more than ${ANNOUNCEMENT_LIMITS.maxShowDays} days away.`);
  }
  return errors;
};

/** The TTL for an announcement and for every "seen" row that points at it. */
export const announcementExpiresAt = (showUntil: number): number =>
  Math.ceil((showUntil + ANNOUNCEMENT_LIMITS.historyDays * DAY_MS) / 1000);

/** The row the portal writes. `publishedAt` is kept from the original on an edit. */
export const buildAnnouncementRow = (
  id: string,
  draft: AnnouncementDraft,
  meta: { publishedAt: number; updatedAt?: number; postedBy?: string; sentTo?: string }
): AnnouncementRow => {
  const linkUrl = draft.linkUrl?.trim();
  const linkLabel = draft.linkLabel?.trim();
  const hasLink = Boolean(linkUrl && linkLabel);
  return {
    user_id: ANNOUNCEMENT_PARTITION,
    notification_id: id,
    title: draft.title.trim(),
    body: draft.body.trim(),
    ...(hasLink ? { link_url: linkUrl, link_label: linkLabel } : {}),
    audience: draft.audience,
    published_at: meta.publishedAt,
    show_until: draft.showUntil,
    ...(meta.updatedAt ? { updated_at: meta.updatedAt } : {}),
    ...(meta.postedBy ? { posted_by: meta.postedBy } : {}),
    ...(meta.sentTo ? { sent_to: meta.sentTo } : {}),
    expires_at: announcementExpiresAt(draft.showUntil),
  };
};

const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);
const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);

/**
 * A stored row as a person sees it, or null when the row is not one we can
 * show (a partial write, a hand edit). Portal-only fields are left behind.
 */
export const toAnnouncement = (row: Record<string, unknown>, seen: boolean): Announcement | null => {
  const id = str(row.notification_id);
  const title = str(row.title);
  const body = str(row.body);
  const publishedAt = num(row.published_at);
  const showUntil = num(row.show_until);
  if (!id || !title || !body || publishedAt === undefined || showUntil === undefined) return null;
  const audience: AnnouncementAudience = row.audience === 'admins' ? 'admins' : 'everyone';
  const linkUrl = str(row.link_url);
  const linkLabel = str(row.link_label);
  const safeLink = linkUrl && linkLabel && isSafeLinkUrl(linkUrl);
  const updatedAt = num(row.updated_at);
  return {
    id,
    title,
    body,
    ...(safeLink ? { linkUrl, linkLabel } : {}),
    audience,
    publishedAt,
    showUntil,
    ...(updatedAt ? { updatedAt } : {}),
    seen,
  };
};

/** True when the portal has revoked the row: nobody sees it. */
export const isRevokedRow = (row: Record<string, unknown>): boolean => num(row.revoked_at) !== undefined;

/**
 * What one person may see, newest first: their audience only, nothing
 * revoked, and nothing the table has not deleted yet but should have (TTL
 * deletes run late).
 */
export const announcementsFor = (
  rows: Record<string, unknown>[],
  seenIds: ReadonlySet<string>,
  opts: { isAdmin: boolean; now: number }
): Announcement[] =>
  rows
    .filter((row) => {
      const ttl = num(row.expires_at);
      return (ttl === undefined || ttl * 1000 > opts.now) && !isRevokedRow(row);
    })
    .map((row) => toAnnouncement(row, seenIds.has(String(row.notification_id))))
    .filter((a): a is Announcement => a !== null)
    .filter((a) => a.audience === 'everyone' || opts.isAdmin)
    .sort((a, b) => b.publishedAt - a.publishedAt);

export interface AnnouncementPopup {
  /** The newest unseen announcement still inside its show-until date. */
  current: Announcement | null;
  /** How many other unseen ones are waiting behind it. */
  earlier: number;
  /** Every id the pop-up stands for: closing it marks all of them seen. */
  ids: string[];
}

export const pickAnnouncementPopup = (announcements: Announcement[], now: number): AnnouncementPopup => {
  const waiting = announcements
    .filter((a) => !a.seen && a.showUntil > now)
    .sort((a, b) => b.publishedAt - a.publishedAt);
  return { current: waiting[0] ?? null, earlier: Math.max(0, waiting.length - 1), ids: waiting.map((a) => a.id) };
};
